// One flagged test on the Timeline, drawn as the demo's trend card (scrips-clinician-demo ?view=timeline, 4 Nov): a sentence
// title ("ApoB up from 0.92 to 1.04 since 2025") with the test and unit beside it, "<test> · latest <date>", the latest value
// large with the flag in words and a mark ("Above lab range"), "Up 0.12 since <date>", and the results in the window as an
// ECharts line with a light area. Every number is the patient's own result; nothing is estimated. Colours are tokens.
import { useMemo } from 'react';
import { CircleCheck } from 'lucide-react';
import EChart from '@/analytics-hub/chart/echart';
import {
  changeSinceFirst,
  changeWords,
  formatDay,
  positionWords,
  rangePosition,
  trendCardOption,
  trendTitle,
  type LabTrendCardModel,
  type RangePosition,
  type TimelineRange,
} from '@/patients/lib/lab-trend-cards';
import { labInstant } from '@/patients/lib/lab-results';
import { FlagChip } from '../labs/lab-analyte-row';
import { MarkIcon } from '../timeline-lanes';
import { useTokenColors } from './chart-colors';
import { useCardPoints } from './use-card-points';

const CHART_HEIGHT = 180;

/** A result written as a plain number: optional sign, digits, optional decimals. */
const PLAIN_NUMBER = /^[+-]?\d+(\.\d+)?$/;

/** The flag's mark: the lanes' triangles above / below, a check within (the demo's). */
function PositionMark({ position }: { position: RangePosition }) {
  if (position === 'above') return <MarkIcon shape="up" size={14} />;
  if (position === 'below') return <MarkIcon shape="down" size={14} />;
  return <CircleCheck className="size-4 flex-none" color="var(--status-good)" aria-hidden="true" />;
}

export interface LabTrendCardProps {
  patientId: string;
  card: LabTrendCardModel;
  labsByRoot: ReadonlyMap<string, string>;
  range: TimelineRange;
}

export function LabTrendCard({ patientId, card, labsByRoot, range }: LabTrendCardProps) {
  const { row } = card;
  const { points, loading, failed } = useCardPoints(patientId, card, labsByRoot, range);
  const colors = useTokenColors();
  // Until the history answers, or when it fails, the card's own latest result places the value against the range.
  // Only a plain number counts ("1,200", "<0.5" or "positive" place nothing: parseFloat would read "1,200" as 1).
  const ownValue = PLAIN_NUMBER.test(row.value.trim()) ? Number(row.value.trim()) : null;
  const last = points.length > 0 ? points[points.length - 1] : null;
  const latest = last ? last.value : ownValue;
  // The large number, the flag and the critical state all come from one result: the last history point once the
  // history answers (as the lab wrote it, with its own flag), else the row. The history keeps only points in the
  // row's unit (trendPointsInRange), so the row's unit fits both.
  const shownValue = last?.text ?? (last ? String(last.value) : row.value);
  // The row's own result keeps the row's flag when the history point carries none, so an HH / LL row never loses its
  // critical mark; any other result shows only its own flag. "Own result": the same observation (the row id is the
  // observation id, toAnalyte), or the same instant with the same value, as a number or as the lab wrote it ("1,200").
  const sameInstant = !!last && last.at === labInstant(row.collectedAt);
  const isRowResult =
    !!last &&
    ((!!last.id && last.id === row.id) ||
      (sameInstant && ((ownValue !== null && last.value === ownValue) || (!!last.text && last.text === row.value))));
  const shownFlag = last ? (last.flag ?? (isRowResult ? row.flag : null)) : row.flag;
  const position = latest === null ? null : rangePosition(latest, row.refLow, row.refHigh);
  const where = positionWords(position);
  const change = changeWords(changeSinceFirst(points));
  const critical = shownFlag?.critical === true;
  // The lab's own flag (FlagChip, red when critical): always for a critical result, and in place of the range words when the
  // lab gave no numeric range to place the value against.
  const labFlag = shownFlag && (critical || !position) ? shownFlag : null;
  const title = trendTitle(row.display, points, shownValue, row.unit, position);
  const chartLabel = `${row.display}, ${points.length} results in this period`;
  const meta = [row.display, row.unit].filter(Boolean).join(' · ');
  const chartOption = useMemo(() => trendCardOption(points, colors, chartLabel), [points, colors, chartLabel]);

  return (
    <article
      data-testid="lab-trend-card"
      data-critical={critical ? 'true' : undefined}
      className="flex min-w-0 flex-col gap-1 rounded-lg border border-border-card bg-surface-card p-4"
      aria-label={`${row.display} trend`}
    >
      <header className="flex items-start justify-between gap-3">
        <h4 className="m-0 min-w-0 truncate text-base font-semibold text-text-primary" title={title}>
          {title}
        </h4>
        {/* The title wins the space; the meta takes at most half and truncates, so it never runs past the card. */}
        <span data-testid="lab-trend-meta" className="min-w-0 max-w-1/2 truncate text-xs text-text-secondary" title={meta}>
          {meta}
        </span>
      </header>
      <p className="m-0 text-xs text-text-secondary">
        {row.display} · latest {formatDay(card.date)}
      </p>
      <p className="m-0 flex flex-wrap items-baseline gap-x-2">
        <span data-testid="lab-trend-value" className={critical ? 'text-2xl font-semibold text-status-error' : 'text-2xl font-semibold text-text-primary'}>
          {shownValue}
        </span>
        {row.unit ? <span className="text-sm text-text-secondary">{row.unit}</span> : null}
        {(where && position) || labFlag ? (
          <span data-testid="lab-trend-position" className="inline-flex items-center gap-2 self-center text-sm text-text-primary">
            {where && position ? (
              <span className="inline-flex items-center gap-1">
                <PositionMark position={position} />
                {where}
              </span>
            ) : null}
            {labFlag ? <FlagChip flag={labFlag} /> : null}
          </span>
        ) : null}
      </p>
      <p data-testid="lab-trend-change" className="m-0 text-sm text-text-secondary">
        {loading
          ? 'Loading earlier results…'
          : failed
            ? "Couldn't load earlier results."
            : (change ?? (row.range ? `Lab range ${row.range}` : 'One result in this period'))}
      </p>
      {points.length >= 2 ? (
        <span data-testid="lab-trend-line" className="block">
          <EChart option={chartOption} height={CHART_HEIGHT} ariaLabel={chartLabel} />
        </span>
      ) : null}
    </article>
  );
}
